import { Box, IcCashierClear, IcCashierSearch, Text } from '@fozito/common-ui';
import { AutoComplete, Input, InputRef, Row, Spin } from 'antd';
import { AxiosResponse } from 'axios';
import { debounce } from 'lodash';
import { useButtonAddCustomerRef } from 'modules/customer/widgets/AddCustomer';
import { ISearchCustomerItem } from 'modules/customer/widgets/AddCustomer/type';
import NotFoundCustomerBox from 'modules/gift-card/pages/GiftCardList/ModalAddGiftCard/NotFoundCustomerBox';
import giftCardService from 'modules/gift-card/services';
import giftCardApis from 'modules/gift-card/services/apis';
import { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { formatPhone } from 'utils/formatPhone';
import { usePointExchangeDollar } from '../../payments/MultiPayment/ModalMultiplePayments/helpers';
import { cashierCtrl } from '../../cashier.controller';
import { validate as validateUUID } from 'uuid';

export const unknownCustomerId = '_unknownCustomerId';

// @ts-ignore
export const unknownCustomerItem: ISearchCustomerItem = {
  id: unknownCustomerId,
  email: '',
  name: 'Unknown Customer',
  phone: '0000000000',
  numberVisit: 0,
  point: 0,
};
export const maskPhone = (val: string) => {
  if (!val) return '';
  return '(***) ***-' + val.slice(val.length - 4, val.length);
};
type ICusOption = {
  value: string;
  name: string;
  keywords: string[];
  label: JSX.Element;
};
export type ISearchCustomer = {
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerPoint: number;
};

type CustomerSearchInputProps = {
  v2NewApt?: boolean;
  value?: ISearchCustomer;
  onChange?: (data: ISearchCustomer | null) => void;
};

const CustomerSearchInput = ({ value }: CustomerSearchInputProps) => {
  const exchangeLoyalty = usePointExchangeDollar();
  const inputRef = useRef<InputRef>(null);
  const [open, setOpen] = useState(false);
  const buttonAddRef = useButtonAddCustomerRef();
  const customerStore = giftCardService.slice.selectors.initialSearchCustomers();
  const [customersFilter, setCustomersFilter] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const initialOptions = useMemo(() => {
    const data: ICusOption[] = customerStore?.map(o => {
      const point = +o?.point || 0;
      const phone = maskPhone(o?.phone || '');
      return ({
        value: o.id,
        name: o.name || '',
        keywords: [o.name?.toLocaleLowerCase(), phone],
        label: renderLabel(o.name || '', point > 1 ? `${point} points` : point > 0 ? `${point} point` : '', phone),
      });
    }) || [];
    return data;
  }, [customerStore]);

  const [searchText, setSearchText] = useState<string>('');

  const optionsFilter = useMemo(() => customersFilter?.map(o => {
    const point = +o?.point || 0;
    const phone = maskPhone(o?.phone || '');
    return ({
      value: o.id,
      name: o.name,
      label: renderLabel(o.name, point > 1 ? `${point} points` : point > 0 ? `${point} point` : '', phone),
    });
  }) || [], [customersFilter]);

  const options = useMemo(() => {
    if (!searchText) return initialOptions;
    return optionsFilter;
  }, [initialOptions, searchText, optionsFilter]);

  const handleClearCustomer = () => cashierCtrl.chooseCustomer(null);

  const handleSwitchCustomer = (data: string) => {
    inputRef.current?.blur();
    setSearchText('');
    const result: ISearchCustomerItem = (searchText ? customersFilter : customerStore)?.find((item) => item.id === data);
    if (!result) return;
    cashierCtrl.chooseCustomer(result);
  };

  const getList = useCallback(debounce(async (search: string) => {
    if (!search?.trim()) return setLoading(false);
    if (validateUUID(search)) return setLoading(false);
    try {
      const res: AxiosResponse<{ data: { content: ISearchCustomerItem[] } }> = await giftCardApis.giftCard.getCustomersFilter(search);
      if (res.data.data.content) {
        setCustomersFilter(res.data.data.content);
      }
    } catch (error) { }
    finally {
      setLoading(false);
    }
  }, 500), []);

  const onOpenAddNew = ({ phone, name }: { phone?: string, name?: string }) => {
    buttonAddRef.current?.open({ phone: phone ? formatPhone(phone) : '', name });
    setSearchText('');
  };

  return (
    <Box display='flex' flexDirection='column'>
      <Container wrap={false} align={'middle'} style={{ gap: '4px' }} className='CustomerSearchInput'>
        <div className="container-input">
          <AutoComplete
            style={{ width: '100%' }}
            options={options}
            dropdownRender={(menu) => (<>
              {searchText && loading ? <Row align={'middle'} justify={'center'}><Text><Spin /></Text></Row> : menu}
            </>)}
            notFoundContent={<NotFoundCustomerBox searchText={searchText} onOpenAddNew={onOpenAddNew} />}
            onSelect={handleSwitchCustomer}
            value={searchText}
            open={open}
            onDropdownVisibleChange={setOpen}
            maxLength={11}
            rootClassName='zoom-ui'
            onChange={text => {
              if (text) setLoading(true);
              setSearchText(text);
              getList(text);
            }}
          >
            <Input
              ref={inputRef}
              suffix={value?.customerPhone ? <IcCashierClear onClick={handleClearCustomer} fill='#fff' /> : <IcCashierSearch fill='#fff' />}
              placeholder={`${value?.customerName ? `${value?.customerName} - ${value?.customerPhone ? maskPhone(value?.customerPhone) : formatPhone('00000000000')}` : 'Customer Name/Phone'}`}
            />
          </AutoComplete>
        </div>
      </Container>
      <CustomerBox>
        <Box display='flex' flexDirection='column' flex={1} alignItems='flex-start'>
          <Text title_h5_24_semibold>{value?.customerName || 'UNKNOWN CUSTOMER'}</Text>
        </Box>
        <Text title_h7_20_bold style={{ color: '#D2464F', fontWeight: '500' }}>{value?.customerPoint || 0} points = {formatCurrency(exchangeLoyalty.exchangeDollar(value?.customerPoint || 0))}</Text>
      </CustomerBox>
    </Box>
  );
};
export default CustomerSearchInput;
const CustomerBox = styled(Box)`
  display: flex;
  padding: 0px 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  background: #FDF6F4;
`;
const Container = styled(Row)`
display: flex;
margin-top: 4px;
padding: 16px;
padding-top: 12px;
flex-direction: column;
justify-content: center;
align-items: center;
align-self: stretch;
background: #E8FBFF;

  .ant-select-selection-placeholder, input.ant-input {
    font-family: Poppins;
    font-size: 22px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    padding-left: 16px;
  }
  
  .container-input {
    display: flex;
    flex: 1;
    align-items: center;
    position: relative;
    align-self: stretch;
    input.ant-input::placeholder {
      color: #1D2129;
    }
  }
  .ant-select-single {
    height: unset;
  }
  .ant-input-affix-wrapper {
    height: 56px;
    background: transparent;
    border: none;
    border-radius: 100px;
    padding: 0;
    overflow: hidden;
  }
  .ant-select-selector {
    border-radius: 100px;
    border: 3px solid #2D6772;
    background: #FFF;
  }

  .ant-input-suffix {
    background: #2D6772;
    zoom: 1.2;
    padding:0 8px;
    padding-right: 16px;
  }
}
`;

const renderLabel = (name: string, point: string, tel: string) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
      }}
    >
      <Text>
        {name}
      </Text>
      <Text content_content_2_16_medium style={{ color: '#D2464F', fontWeight: '500' }} >
        {point}
      </Text>
    </div>
    <div>
      <Text>
        {tel}
      </Text>
    </div>
  </div>
);
